import React, { useEffect, useState } from 'react';
import { TextField, Grid, Typography, Button, Checkbox } from '@mui/material';
import CustomSnackbar from '../elements/CustomSnackbar';
import CountrySelect from './CountrySelect';
import { useUser } from '../../contexts/UserContext';
import StripeCardInput from './StripeCardInput';
import { BillingFormProps } from '../../types';
import { getPaymentMethodAcceptedCreditCardBrands } from '../../hooks/cart/AcceptedPaymentMethodBrands';
import { getLocationId } from '../../utils';
import { usePublicSettings } from '../../hooks/usePublicSettings';
import { capitalizeFirstLetter } from '../../utils';

const BillingForm: React.FC<BillingFormProps> = ({ billingInfo, setBillingInfo, handleNext }) => {
  const { user } = useUser();
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [stripeData, setStripeData] = useState({ token: '', brand: '', tokenPreferred: '' });
  const [acceptedPaymentMethodCreditCardBrands, setAcceptedPaymentMethodCreditCardBrands] = useState<string[]>([]);
  const { companyName } = usePublicSettings();
  const locationUUID = getLocationId();

  useEffect(() => {
    if (!locationUUID) return;

    (async () => {
      const acceptedMethodsResponse = await getPaymentMethodAcceptedCreditCardBrands(locationUUID);
      console.log('acceptedMethodsResponse', acceptedMethodsResponse);
      if (acceptedMethodsResponse.statusCode === 200)
        setAcceptedPaymentMethodCreditCardBrands(acceptedMethodsResponse.data);
    })();
  }, [locationUUID]);

  const handleBillingInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked } = event.target;
    setBillingInfo((prev) => ({
      ...prev,
      [name]: name === 'savePreferred' ? checked : value,
    }));
  };

  const handleCountryChange = (newCountryCode: string) => {
    setBillingInfo((prev) => ({ ...prev, country: newCountryCode }));
  };

  const handleNextClick = () => {
    if (
      !billingInfo.firstName ||
      !billingInfo.lastName ||
      !billingInfo.email ||
      !billingInfo.address ||
      !billingInfo.phone ||
      !billingInfo.city ||
      !billingInfo.zipCode ||
      !billingInfo.country
    ) {
      return setSnackbar({ open: true, message: 'Please fill all required fields', severity: 'error' });
    }

    if (!stripeData.token || !stripeData.brand) {
      return setSnackbar({ open: true, message: 'Invalid credit card details', severity: 'error' });
    }

    if (!acceptedPaymentMethodCreditCardBrands.includes(stripeData.brand)) {
      return setSnackbar({
        open: true,
        message: `${companyName} does not accept ${capitalizeFirstLetter(stripeData.brand)} at this location.`,
        severity: 'error',
      });
    }

    handleNext(stripeData);
  };

  useEffect(() => {
    if (user?.email) {
      setBillingInfo((prev) => ({ ...prev, email: user.email }));
    }
  }, [user, setBillingInfo]);

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return (
    <div>
      <Typography variant="h6">Billing Information</Typography>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <TextField
            label="First Name"
            required
            fullWidth
            name="firstName"
            value={billingInfo.firstName}
            onChange={handleBillingInputChange}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            label="Last Name"
            required
            fullWidth
            name="lastName"
            value={billingInfo.lastName}
            onChange={handleBillingInputChange}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            label="Email"
            required
            fullWidth
            name="email"
            value={billingInfo.email}
            onChange={handleBillingInputChange}
            disabled
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            label="Phone"
            required
            fullWidth
            name="phone"
            value={billingInfo.phone}
            onChange={handleBillingInputChange}
          />
        </Grid>
        <Grid item xs={12}>
          <CountrySelect value={billingInfo.country} onChange={handleCountryChange} />
        </Grid>
        <Grid item xs={6}>
          <TextField
            label="State/Province"
            required
            fullWidth
            name="state"
            value={billingInfo.state}
            onChange={handleBillingInputChange}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            label="City"
            required
            fullWidth
            name="city"
            value={billingInfo.city}
            onChange={handleBillingInputChange}
          />
        </Grid>
        <Grid item xs={12} lg={8}>
          <TextField
            label="Address"
            required
            fullWidth
            name="address"
            value={billingInfo.address}
            onChange={handleBillingInputChange}
          />
        </Grid>
        <Grid item xs={6} lg={4}>
          <TextField
            label="Zip/Postal"
            required
            fullWidth
            name="zipCode"
            value={billingInfo.zipCode}
            onChange={handleBillingInputChange}
          />
        </Grid>
        <Grid item xs={12}>
          {billingInfo.firstName &&
            billingInfo.lastName &&
            billingInfo.email &&
            billingInfo.address &&
            billingInfo.phone &&
            billingInfo.city &&
            billingInfo.zipCode &&
            billingInfo.country && <StripeCardInput setStripeData={setStripeData} billingInfo={billingInfo} />}
        </Grid>
        <Grid item xs={12}>
          <Typography variant="h6">
            <Checkbox
              checked={billingInfo.savePreferred}
              onChange={(e) => handleBillingInputChange(e)}
              name="savePreferred"
              sx={{
                paddingLeft: '0px',
                paddingTop: '4px',
              }}
            />
            Save as preferred payment method.
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Button variant="contained" color="primary" onClick={handleNextClick} className="next-button">
            Next
          </Button>
        </Grid>
      </Grid>

      <CustomSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        handleClose={handleCloseSnackbar}
      />
    </div>
  );
};

export default BillingForm;
